const scopeKey = (scope) => typeof scope === "string" ? scope : scope?.kind === "care_team" ? "me:care" : "me";
/**
 * Central TanStack Query key factory shared by the web and mobile clients.
 *
 * Every query key in the HealthOS frontends lives here so the two clients
 * never drift: a key written in web is identical to the one mobile reads, and
 * invalidation helpers can target a single canonical tuple.
 *
 * Conventions:
 *  - Static keys are `readonly [string, ...string[]]` tuples.
 *  - Parameterised keys are functions returning the same shape.
 *  - Prefix keys (used for partial invalidation) end with a stable leading
 *    segment so `invalidateQueries({ queryKey: prefix })` catches every
 *    parameterised child.
 */
export const queryKeys = {
    // Ambient / Today surface
    ambient: ["ambient"],
    outcome: ["me", "outcome"],
    heartbeatDecisions: ["heartbeat", "decisions"],
    heartbeatCard: ["heartbeat", "card"],
    heartbeatGraph: ["heartbeat", "graph"],
    heartbeatPasses: ["heartbeat", "passes"],
    whmaGraph: ["whma", "graph"],
    snapshot: ["me", "snapshot"],
    snapshotNarrative: ["me", "snapshot", "narrative"],
    snapshotDetail: ["me", "snapshot", "detail"],
    snapshotGoal: ["me", "snapshot", "goal"],
    // Day view and its calendar. Both sit under `snapshot`, so invalidating the
    // scaffold catches them too; the prefixes let a Logbook write reach them alone.
    snapshotDayPrefix: ["me", "snapshot", "day"],
    /** The weekly scaffold as it stood at the end of a past day. */
    snapshotAsOf: (date) => ["me", "snapshot", "as-of", date],
    snapshotDay: (date) => ["me", "snapshot", "day", date],
    snapshotCalendarPrefix: ["me", "snapshot", "calendar"],
    snapshotCalendar: (from, to) => ["me", "snapshot", "calendar", from, to],
    score: ["me", "score"],
    grants: ["me", "grants"],
    profile: ["profile"],
    explainerDismissals: ["explainers", "dismissals"],
    challengeGroupProgress: (enrollmentId) => ["challenges", "group-progress", enrollmentId],
    challengeGroupProgressPrefix: ["challenges", "group-progress"],
    challengeGroupProgressTimeline: (enrollmentId) => ["challenges", "group-progress-timeline", enrollmentId],
    challengeWorkspace: ["challenges", "workspace"],
    companyAwardStats: ["challenges", "company-award-stats"],
    routeAwardCollection: ["challenges", "route-award-collection"],
    challengePreferences: ["challenges", "preferences"],
    companyWalkingChallenge: ["challenges", "company"],
    companyChallengeRouteVote: ["challenges", "company", "next-route-vote"],
    challengeHistory: ["challenges", "history"],
    pastChallenge: (challengeId) => ["challenges", "history", challengeId],
    challengeWrapUp: (challengeId) => ["challenges", "history", challengeId, "wrap-up"],
    challengeLeaderboards: ["challenges", "leaderboards"],
    challengeLeaderboard: (instanceId) => ["challenges", "leaderboards", instanceId],
    challengeLeaderboardPage: (instanceId, limit) => ["challenges", "leaderboards", instanceId, "top", limit],
    challengeLeaderboardGroup: (instanceId, groupId) => ["challenges", "leaderboards", instanceId, "groups", groupId],
    walkingGroups: ["walking-groups"],
    walkingGroupMembers: (groupId) => ["walking-groups", groupId, "members"],
    // Library
    library: ["library", "items"],
    libraryPage: (query) => ["library", "items", query],
    libraryRecommendations: ["library", "recommendations"],
    libraryCollection: (collectionId) => ["library", "collections", collectionId],
    libraryHubs: ["library", "hubs"],
    courses: ["courses"],
    course: (courseId) => ["courses", courseId],
    libraryCompletions: (kind) => ["library", "completions", kind ?? "all"],
    brief: ["brief", "today"],
    briefHistory: ["brief", "history"],
    // Personal Resets (mobile-only for now).
    personalResetsMine: ["personal-resets", "mine"],
    personalResetAudioLibrary: ["personal-resets", "audio-library"],
    personalResetsShared: ["personal-resets", "shared"],
    personalResetsCommunity: ["personal-resets", "community"],
    personalReset: (id) => ["personal-resets", "item", id],
    // Coaching: the coachee's own programme, and the coach's workspace.
    /** Every coaching query, and a member's own coaching programme. */
    coaching: ["coaching"],
    /** A member's own programme of one kind; coaching keeps the root key it always had. */
    coachingMe: (kind = "coaching") => kind === "care_team" ? ["coaching", "care"] : ["coaching"],
    coachingProgrammes: ["coaching", "programmes"],
    coachingSlots: ["coaching", "slots"],
    coachingSlotsOf: (kind = "coaching") => kind === "care_team"
        ? ["coaching", "care", "slots"]
        : ["coaching", "slots"],
    coachCoachees: ["coaching", "coach", "coachees"],
    coachCoachee: (id) => ["coaching", "coach", "coachees", id],
    coachCoacheeSlots: (id) => ["coaching", "coach", "coachees", id, "slots"],
    coachSessions: (from, to) => ["coaching", "coach", "sessions", from, to],
    coachSettings: ["coaching", "coach", "settings"],
    coachAttention: ["coaching", "coach", "attention"],
    /** A coach's unread messages across their coachees, for the nav badge. */
    coachUnread: ["coaching", "coach", "unread"],
    /** The coachee's thread with their coach, or a coach's thread with one coachee. */
    coachingThread: (scope) => ["coaching", "thread", scopeKey(scope)],
    coachingNotes: (scope) => ["coaching", "notes", scopeKey(scope)],
    coachingActions: (scope) => ["coaching", "actions", scopeKey(scope)],
    coachPreRead: (id) => ["coaching", "coach", "coachees", id, "pre-read"],
    coachingWeek: ["coaching", "week"],
    coachAssessments: (id) => ["coaching", "coach", "coachees", id, "assessments"],
    coachingOpsCoachees: ["coaching", "ops", "coachees"],
    coachingOpsCoacheesPage: (query) => ["coaching", "ops", "coachees", query],
    coachingOpsCoachee: (id) => ["coaching", "ops", "coachee", id],
    coachingOpsOverview: ["coaching", "ops", "overview"],
    coachingOpsOverviewOf: (kind) => ["coaching", "ops", "overview", kind],
    coachingOpsCoaches: ["coaching", "ops", "coaches"],
    coachingOpsAttention: ["coaching", "ops", "attention"],
    coachingOpsAttentionOf: (kind) => ["coaching", "ops", "attention", kind],
    coacheeSearch: (q) => ["coaching", "coach", "coachees", "search", q],
    coachingOpsCompanies: ["coaching", "ops", "companies"],
    coachingOpsCohorts: ["coaching", "ops", "cohorts"],
    coachingOpsCohortsOf: (kind) => ["coaching", "ops", "cohorts", kind],
    coachingOpsInvites: (cohortId) => ["coaching", "ops", "cohorts", cohortId, "invites"],
    coachingOpsMembers: (q) => ["coaching", "ops", "members", q],
    // Onboarding + connections
    onboarding: ["onboarding"],
    connections: ["connections"],
    appleHealth: ["apple-health", "status"],
    // Device-local flag: has the user imported their Apple Health weight history
    // yet (gates the one-time import prompt). Mobile-only for now.
    appleHealthWeightsImported: ["apple-health", "weights-imported"],
    // Ephemeral cache slot holding live import progress (windows done / total)
    // so any surface showing the import can render an advancing state. Mobile-only.
    appleHealthWeightImportProgress: ["apple-health", "weight-import-progress"],
    // Health Connect is the Android device-health source. Status is the local
    // Terra user id read; availability is whether the store exists on the device
    // at all; granted is which record types the member actually allowed.
    healthConnect: ["health-connect", "status"],
    healthConnectAvailability: ["health-connect", "availability"],
    healthConnectGrantedPermissions: ["health-connect", "granted-permissions"],
    // Device-local flag: has the member dismissed the challenges Overview ask to
    // link their device health source. Per source, so an iOS dismissal does not
    // silence the Android ask. Mobile-only.
    deviceHealthCtaDismissed: (source) => ["device-health-cta", "dismissed", source],
    // Signals. The prefix form (no hours arg) is used by invalidation to catch
    // every timeline window; the parameterised form is the actual query key.
    signalTimelinePrefix: ["signals", "timeline"],
    signalTimeline: (hours = 48) => ["signals", "timeline", hours],
    // Logbook. Prefix forms (no args) catch every pillar/day-range/filter
    // combination for invalidation after a manual write (edit, delete, flag);
    // the parameterised forms are the actual query keys a tab's hook reads.
    logbookTrendPrefix: ["logbook", "trend"],
    logbookTrend: (pillar, days, date) => ["logbook", "trend", pillar, days, date ?? "now"],
    logbookHistoryPrefix: ["logbook", "history"],
    logbookHistory: (query) => [
        "logbook",
        "history",
        query.pillar ?? "all",
        query.source ?? "all",
        query.type ?? "all",
        query.date ?? "all",
    ],
    // Content
    contentArticle: (id) => ["content", "article", id],
    // Weather
    weatherToday: (lat, lon) => lat != null && lon != null
        ? ["weather", "today", lat, lon]
        : ["weather", "today"],
    // Tracking
    glp1Tracking: ["tracking", "glp1"],
    memberName: (userId) => ["member-name", userId],
    // Onboarding gate (whoami-derived client cache)
    whoamiOnboardingGate: ["whoami-onboarding-gate"],
    // Dev / ops surfaces
    devHeartbeatWhy: ["dev", "heartbeat-why"],
    devArmPerformance: ["dev", "arm-performance"],
    devWhoami: ["dev", "whoami"],
    tweaksPanel: ["me", "tweaks-panel"],
    darkMode: ["me", "dark-mode"],
    operationsScenarios: ["operations", "scenarios"],
    operationsBroadcasts: ["operations", "broadcasts"],
    contactCentreMe: ["contact-centre", "me"],
    contactCentreReset: ["contact-centre", "reset"],
};
/**
 * Community surface keys. `feedRoot` and `feed` are the same tuple: `feedRoot`
 * names the prefix used for broad invalidation and `feed` the exact query, kept
 * distinct so call sites read their intent. Lives here, not in a client, so web
 * and mobile share one namespace rather than reinventing it.
 */
export const communityQueryKeys = {
    feedRoot: ["community", "feed"],
    feed: ["community", "feed"],
    itemReactors: (itemType, itemId) => ["community", "reactors", itemType, itemId],
    // Placeholder key for a disabled reactors query (no item selected yet).
    itemReactorsIdle: ["community", "reactors", "idle"],
    teammates: (query) => ["community", "teammates", query],
    memberProfile: (userId) => ["community", "member-profile", userId],
};
export const notificationQueryKeys = {
    root: ["notifications"],
    list: (includeDismissed = false) => ["notifications", includeDismissed],
};
//# sourceMappingURL=query-keys.js.map